import { useCallback, useEffect, useMemo, useState } from "react"

/**
 * @template {string} T
 * @param {T} collectionURL
 */

export default function hookListFactory(collectionURL, siteOptions = { siteKey: null }) {
  /**
   * @template {string} [S=collectionURL]
   * @param {S} [storeName]
   */

  return (storeName = collectionURL) => ({ fetch, siteSelected, lists }) => {
    const [items, setItems] = useState([])

    const parseParams = useCallback(params => {
      if (siteSelected && siteOptions) {
        const { id: siteId, key: siteKey } = siteSelected
        const { siteKey: overrideSiteKey } = siteOptions
        return {
          ...params,
          [overrideSiteKey || siteKey]: siteId,
        }
      }
      return params
    }, [siteSelected])

    const list = useCallback(async params => {
      const data = await fetch({
        url: `${collectionURL}/all`,
        params: parseParams(params),
      })
      setItems(data)
      return data
    }, [fetch, parseParams])

    return useMemo(() => {
      const store = {
        items,
        list,
        useLoad: params => {
          useEffect(() => {
            list(params)
          }, [params])
          return useCallback(() => list(params), [params])
        },
      }
      return {
        /**
         * @type {{[x in storeName]: store}}
         */
        lists: {
          ...lists || {},
          [storeName]: store,
        },
      }
    }, [items, list, lists])
  }
}

export function useSelectOptions({ items, useLoad }, { value: valueKey = 'id', label: labelKey = 'name' } = {}) {
  useLoad()

  return useMemo(() => items.map(({
    [valueKey]: value,
    [labelKey]: label,
  }) => ({
    value,
    label,
  })), [items, labelKey, valueKey])
}
